/**
 * WEB-1009 — межслойный гард контраста поверхности воронки.
 *
 * Оформление воронки собирается из независимых слоёв (тёплая база → тема →
 * фон → палитра → оверрайд цвета текста). Каждый слой внутри себя
 * согласован, но пару «фон страницы / основной текст» слои задают ПОРОЗНЬ:
 *
 *   • фон страницы кладёт тема (themeToStyle → ключ `background`) или явный
 *     оверрайд фона;
 *   • цвет текста кладёт палитра (paletteToStyle → `--color-text-primary`),
 *     а её текст посчитан бэком от СВОЕЙ surface (readableTextOn), про фон
 *     темы палитра не знает.
 *
 * Отсюда живой баг QA (2026-08-03): тема «Minimalist Dark» (фон #0f0f0f) +
 * светлая палитра (текст #0f172a) = 1.07:1, страница выглядит пустой.
 *
 * Лечение — резолвер эффективной пары ПОСЛЕ всех слоёв плюс гард. Модуль
 * чистый и не знает про React: его зовёт единственная сборка поверхности
 * (buildFunnelSurfaceStyle), поэтому превью конструктора и публичная
 * страница получают один и тот же результат.
 */
import type { CSSProperties } from 'react';
import { contrastRatio, darkenToContrastAll, blendHexOverBackground } from '@/themes/colorUtils';
import { resolveSurfacePageBackground } from './useFunnelSurfaceOnBody';
// WEB-1078 п.2: тот же порог, которым уже подбирается --color-link на
// генерации (themes/index.ts) — гард переиспользует его, а не выдумывает
// второй.
import { LINK_CONTRAST_TARGET } from '@/components/editor/linkColor';

/**
 * Порог РАЗЛИЧИМОСТИ. Ниже него текст и фон человек не различает — слой
 * объективно сломан, и гард вмешивается. Значение — нижняя планка
 * читаемости WCAG (крупный текст / нетекстовые элементы), та же, которой
 * меряются звёзды рейтинга и бейджи (см. colorUtils.darkenToContrastAll).
 *
 * Выше порога решение автора не переписываем: осознанный оверрайд
 * (например #ff0000 на мятном фоне, 3.63:1 — спека WEB-921) остаётся как
 * есть. Гард лечит нечитаемость, а не вкус.
 */
export const SURFACE_TEXT_LEGIBILITY_FLOOR = 3;

/**
 * Куда дожимаем, когда гард сработал. Раз пара сломана — чиним до нормы
 * WCAG AA для основного текста, а не до самой планки различимости.
 */
export const SURFACE_TEXT_CONTRAST_TARGET = 4.5;

/**
 * WEB-1078 — тот же межслойный разрыв, что чинит гард выше, но для
 * --color-text-secondary: палитра выпускает его безусловно (paletteToStyle,
 * если palette.textSecondary задан), посчитан бэком от СВОЕЙ surface
 * (backend/src/ai-funnels/prompts/theme-ids.ts::fixTextSecondary), а фон
 * страницы после всех слоёв кладёт независимый слой (тема/явный фон).
 *
 * Порог НЕ равен порогу основного текста (3 → 4.5) — сознательно ниже:
 * 3.0 это ровно `SECONDARY_CONTRAST_MIN` из backend/theme-ids.ts, которым
 * бэк уже проверяет secondary против его родной surface. Дожимать вторичный
 * текст до 4.5 (нормы ОСНОВНОГО текста) значило бы визуально уравнять его с
 * заголовком — это не читаемость, а стирание задуманной приглушённости
 * (вторичный текст обязан остаться второстепенным, просто различимым).
 *
 * Floor и target здесь совпадают (в отличие от primary, где 3..4.5 —
 * окно для осознанного авторского выбора): у secondary НЕТ отдельно
 * установленной «планки полной нечитаемости» ниже 3 — сама карточка задаёт
 * 3.0 как единственную принятую норму («ни одного случая ниже нормы 3:1»),
 * поэтому чинить нечего дожимать сверх неё, но и триггерить раньше неё
 * не от чего оттолкнуться.
 */
export const SECONDARY_TEXT_LEGIBILITY_FLOOR = 3;
export const SECONDARY_TEXT_CONTRAST_TARGET = 3;

/**
 * WEB-1078 п.2 — тот же межслойный разрыв для --color-link. Ссылка
 * подбирается (themes/index.ts paletteToStyle/themeToStyle) до
 * LINK_CONTRAST_TARGET, но ПРОТИВ [palette.surface, palette.background] или
 * набора поверхностей темы — не против фактического фона страницы после
 * ВСЕХ слоёв (явный оверрайд воронки может перекрасить фон поверх обоих).
 *
 * Floor — тот же общий порог различимости, что у primary/secondary (см.
 * SURFACE_TEXT_LEGIBILITY_FLOOR выше — не второе число, alias). Target —
 * переиспользован LINK_CONTRAST_TARGET (4.6): именно им уже подбирается сам
 * токен, дожимать до другого числа значило бы держать две разные нормы для
 * одной и той же переменной.
 */
export const LINK_TEXT_LEGIBILITY_FLOOR = SURFACE_TEXT_LEGIBILITY_FLOOR;
export const LINK_TEXT_CONTRAST_TARGET = LINK_CONTRAST_TARGET;

/**
 * WEB-1078 п.2 — тот же разрыв для --color-error. Бэк уже гарантирует
 * контраст danger >= TEXT_CONTRAST_MIN (4.5) против ЕГО РОДНОЙ surface
 * (backend/src/ai-funnels/prompts/theme-ids.ts::fixSemanticColor) — ровно та
 * же норма, что и у основного текста. Фронт (paletteToStyle/themeToStyle)
 * либо берёт этот уже проверенный danger как есть, либо для тем считает
 * deriveDanger(surface) без проверки вовсе — в обоих случаях против фона
 * СТРАНИЦЫ после всех слоёв не сверено ни разу.
 *
 * Target переиспользует SURFACE_TEXT_CONTRAST_TARGET (4.5) — та же норма,
 * которой бэк уже мерит danger, а гард выше мерит primary. Отдельного числа
 * под error нет и не нужно: роль та же (текст сообщения), только фон другой.
 */
export const ERROR_TEXT_LEGIBILITY_FLOOR = SURFACE_TEXT_LEGIBILITY_FLOOR;
export const ERROR_TEXT_CONTRAST_TARGET = SURFACE_TEXT_CONTRAST_TARGET;

/** #rgb / #rrggbb внутри произвольного CSS-значения (точки градиента и пр.). */
const HEX_IN_CSS = /#(?:[0-9a-fA-F]{6}|[0-9a-fA-F]{3})(?![0-9a-fA-F])/g;

/**
 * Все измеримые фоны, на которых реально окажется основной текст.
 *
 * Градиент даёт несколько точек — порог обязан держаться на каждой (тот же
 * урок, что в WEB-916: считать против одной поверхности мало).
 *
 * Значения с `url(` (фото-фон) и `var(` (ссылка на переменную) намеренно
 * НЕ измеряем: под фотографией цвет текста решает вуаль (WEB-872), а не
 * этот гард, и угадывать там нечего.
 */
export function measurableBackgrounds(cssBackground: string): string[] {
  if (/url\(|var\(/i.test(cssBackground)) return [];
  return cssBackground.match(HEX_IN_CSS) ?? [];
}

export interface EffectiveSurfacePair {
  /** Цвет основного текста после наложения всех слоёв. */
  text: string;
  /** Измеримые точки фона страницы после наложения всех слоёв. */
  backgrounds: string[];
}

/**
 * Эффективная пара «фон/текст» собранной поверхности.
 *
 * Фон берём ТЕМ ЖЕ резолвером, которым публичная страница красит html/body
 * (WEB-989) — иначе гард мерил бы одно, а браузер показывал другое, то есть
 * ровно тот класс расхождения, который эта карточка и лечит.
 *
 * null = мерить нечего (нет текста, либо фон неизмерим).
 */
export function resolveEffectiveSurfacePair(
  style: CSSProperties,
): EffectiveSurfacePair | null {
  const raw = style as Record<string, unknown>;
  const textCandidate = raw['--color-text-primary'] ?? raw.color;
  if (typeof textCandidate !== 'string' || textCandidate.length === 0) {
    return null;
  }
  const background = resolveSurfacePageBackground(style);
  if (typeof background !== 'string' || background.length === 0) return null;

  const backgrounds = measurableBackgrounds(background);
  if (backgrounds.length === 0) return null;
  if (measurableBackgrounds(textCandidate).length === 0) return null;

  return { text: textCandidate, backgrounds };
}

/**
 * Эффективная пара «вторичный текст/фон» собранной поверхности (WEB-1078).
 *
 * Зеркало resolveEffectiveSurfacePair, но без фолбэка на plain `color` — у
 * вторичного текста нет прямого CSS-аналога (`color` всегда несёт основной),
 * секондари живёт только в переменной --color-text-secondary.
 */
export function resolveEffectiveSecondaryTextPair(
  style: CSSProperties,
): EffectiveSurfacePair | null {
  return resolveEffectiveVarPair(style, '--color-text-secondary');
}

/**
 * Читает `varName` как эффективную пару «текст-переменная / фон страницы».
 * Общий механизм под resolveEffectiveSecondaryTextPair/*LinkPair/*ErrorPair —
 * все три читают ОДНУ CSS-переменную безусловно (в отличие от
 * resolveEffectiveSurfacePair, у которого есть отдельный фолбэк на `color`).
 */
function resolveEffectiveVarPair(
  style: CSSProperties,
  varName: string,
): EffectiveSurfacePair | null {
  const raw = style as Record<string, unknown>;
  const textCandidate = raw[varName];
  if (typeof textCandidate !== 'string' || textCandidate.length === 0) {
    return null;
  }
  const background = resolveSurfacePageBackground(style);
  if (typeof background !== 'string' || background.length === 0) return null;

  const backgrounds = measurableBackgrounds(background);
  if (backgrounds.length === 0) return null;
  if (measurableBackgrounds(textCandidate).length === 0) return null;

  return { text: textCandidate, backgrounds };
}

/**
 * Эффективная пара «ссылка/фон страницы» собранной поверхности (WEB-1078 п.2).
 */
export function resolveEffectiveLinkPair(style: CSSProperties): EffectiveSurfacePair | null {
  return resolveEffectiveVarPair(style, '--color-link');
}

/**
 * Эффективная пара «цвет ошибки/фон страницы» собранной поверхности
 * (WEB-1078 п.2).
 */
export function resolveEffectiveErrorPair(style: CSSProperties): EffectiveSurfacePair | null {
  return resolveEffectiveVarPair(style, '--color-error');
}

/** Итоговый контраст пары — по самой сложной точке фона. */
export function surfacePairContrast(pair: EffectiveSurfacePair): number {
  return Math.min(...pair.backgrounds.map((bg) => contrastRatio(pair.text, bg)));
}

/**
 * Причина срабатывания пишется в консоль (требование карточки): иначе
 * «почему текст не того цвета, который я задал» не отлаживается вообще.
 * Дедуп по паре — сборка поверхности зовётся на каждый рендер превью.
 */
const reported = new Set<string>();

type GuardKind = 'primary' | 'secondary' | 'link' | 'error';

const GUARD_KIND_META: Record<
  GuardKind,
  { card: string; floor: number; label: string; role: string }
> = {
  primary: { card: 'WEB-1009', floor: SURFACE_TEXT_LEGIBILITY_FLOOR, label: 'основного текста', role: 'Цвет текста' },  // i18n-allow: диагностика в консоль разработчика
  secondary: {
    card: 'WEB-1078',
    floor: SECONDARY_TEXT_LEGIBILITY_FLOOR,
    label: 'вторичного текста',  // i18n-allow: диагностика в консоль разработчика
    role: 'Цвет текста',  // i18n-allow: диагностика в консоль разработчика
  },
  link: { card: 'WEB-1078', floor: LINK_TEXT_LEGIBILITY_FLOOR, label: 'ссылки', role: 'Цвет ссылки' },  // i18n-allow: диагностика в консоль разработчика
  error: { card: 'WEB-1078', floor: ERROR_TEXT_LEGIBILITY_FLOOR, label: 'сообщения об ошибке', role: 'Цвет ошибки' },  // i18n-allow: диагностика в консоль разработчика
};

function reportGuard(
  kind: GuardKind,
  from: string,
  to: string,
  backgrounds: string[],
  ratio: number,
): void {
  const key = `${kind}:${from}->${to}@${backgrounds.join(',')}`;
  if (reported.has(key)) return;
  reported.add(key);
  const { card, floor, label, role } = GUARD_KIND_META[kind];
  console.warn(
    `[${card}] Контраст ${label} воронки ${ratio.toFixed(2)}:1 на фоне ` +  // i18n-allow: диагностика в консоль разработчика
      `${backgrounds.join(', ')} — ниже порога различимости ${floor}:1. ` +  // i18n-allow: диагностика в консоль разработчика
      `${role} ${from} дожат до ${to}. ` +  // i18n-allow: диагностика в консоль разработчика
      'Причина: слои оформления (тема/палитра/оверрайд) задали фон и текст ' +  // i18n-allow: диагностика в консоль разработчика
      'независимо друг от друга.',  // i18n-allow: диагностика в консоль разработчика
  );
}

/**
 * Общий шаг гарда под secondary/link/error: читает свою переменную, дожимает
 * при нарушении, репортит. Primary держит отдельную ветку выше — у него есть
 * фолбэк на `color`, которого у остальных трёх ролей нет.
 */
function applyVarGuard(
  style: CSSProperties,
  next: Record<string, unknown> | null,
  kind: GuardKind,
  varName: string,
  resolvePair: (style: CSSProperties) => EffectiveSurfacePair | null,
  floor: number,
  target: number,
): Record<string, unknown> | null {
  const pair = resolvePair(style);
  if (!pair) return next;
  const ratio = surfacePairContrast(pair);
  if (ratio >= floor) return next;
  const repaired = darkenToContrastAll(pair.text, pair.backgrounds, target);
  if (repaired === pair.text) return next;
  reportGuard(kind, pair.text, repaired, pair.backgrounds, ratio);
  return { ...(next ?? (style as Record<string, unknown>)), [varName]: repaired };
}

/**
 * Гард: если после наложения ВСЕХ слоёв основной текст не отличим от фона
 * страницы — дожать текст до читаемого, сохранив оттенок.
 *
 * Оттенок тянет `darkenToContrastAll` (механизм WEB-916): шагаем к белому на
 * тёмном фоне и к чёрному на светлом до первого значения, которое держит
 * порог. В пределе это тот же выбор, что делает `pickOnPrimaryColor` для
 * текста на кнопке, но промежуточные шаги сохраняют цвет автора.
 *
 * Чистая функция: входной стиль не мутируется (возвращается новый объект),
 * при отсутствии нарушения возвращается исходная ссылка.
 */
export function enforceSurfaceTextContrast(style: CSSProperties): CSSProperties {
  let next: Record<string, unknown> | null = null;

  const primaryPair = resolveEffectiveSurfacePair(style);
  if (primaryPair) {
    const ratio = surfacePairContrast(primaryPair);
    if (ratio < SURFACE_TEXT_LEGIBILITY_FLOOR) {
      const repaired = darkenToContrastAll(
        primaryPair.text,
        primaryPair.backgrounds,
        SURFACE_TEXT_CONTRAST_TARGET,
      );
      if (repaired !== primaryPair.text) {
        reportGuard('primary', primaryPair.text, repaired, primaryPair.backgrounds, ratio);
        next = { ...(style as Record<string, unknown>), '--color-text-primary': repaired };
        // `color` правим только если его выставил слой-нарушитель (палитра
        // или оверрайд). Тема кладёт лишь переменную — не расширяем зону
        // поражения фикса на элементы, которые сегодня наследуют цвет извне.
        if (typeof (style as Record<string, unknown>).color === 'string') {
          next.color = repaired;
        }
      }
    }
  }

  // WEB-1078 п.1: та же проверка для вторичного текста — независимая
  // переменная, независимый фикс. Читаем из ИСХОДНОГО style (фон страницы
  // фикс primary не трогает), пишем поверх next, если primary уже что-то
  // поправил.
  next = applyVarGuard(
    style,
    next,
    'secondary',
    '--color-text-secondary',
    resolveEffectiveSecondaryTextPair,
    SECONDARY_TEXT_LEGIBILITY_FLOOR,
    SECONDARY_TEXT_CONTRAST_TARGET,
  );

  // WEB-1078 п.2: --color-link и --color-error та же дыра — обе переменные
  // подобраны против поверхности темы/палитры на генерации, не против
  // фактического фона страницы после всех слоёв.
  next = applyVarGuard(
    style,
    next,
    'link',
    '--color-link',
    resolveEffectiveLinkPair,
    LINK_TEXT_LEGIBILITY_FLOOR,
    LINK_TEXT_CONTRAST_TARGET,
  );
  next = applyVarGuard(
    style,
    next,
    'error',
    '--color-error',
    resolveEffectiveErrorPair,
    ERROR_TEXT_LEGIBILITY_FLOOR,
    ERROR_TEXT_CONTRAST_TARGET,
  );

  return next === null ? style : (next as CSSProperties);
}

/**
 * WEB-1078 п.4 — блок может нести СОБСТВЕННЫЙ фон (EditorBlock.background,
 * blockBackgroundStyle.tsx: none/solid/translucent/blur/glass), независимый
 * от фона страницы. Текст внутри блока красит переменная страницы
 * (--color-text-primary) — смена темы её меняет, но НИЧЕГО не трогает
 * background.color конкретных блоков. Пара может рассыпаться молча.
 *
 * НЕ фикс. Гарды выше чинят страничную пару молча — это оправдано, потому
 * что «фон страницы» и «системный текст» не авторский выбор конкретного
 * блока, это композиция слоёв, где ни один слой не в курсе другого. Фон
 * блока — наоборот, ЯВНО заданный человеком цвет ОДНОГО блока (тот же
 * принцип, каким primary-гард НЕ трогает осознанный оверрайд текста выше
 * порога различимости — см. док resolveEffectiveSurfacePair). Переписать его
 * тихо в момент смены темы означало бы удивить автора ИЗМЕНЕНИЕМ, которое он
 * не совершал и не увидит — ровно тот класс бага, который вся эта карточка
 * лечит. Поэтому находим риск и отдаём вызывающему (конструктору) —
 * предупредить человека в момент действия, пока его легко исправить или
 * отменить.
 *
 * Скоуп: только `style: 'solid'`. translucent/blur/glass — намеренно
 * ПОЛУПРОЗРАЧНЫЕ подложки (WEB-955, дефолтные плотности 0.6/0.35/0.25) —
 * фон страницы всегда проступает сквозь них, поэтому их эффективный цвет
 * зависит от РЕЗОЛВНУТОГО фона страницы намного сильнее и с меньшей
 * предсказуемостью (blur/glass ещё и просят backdrop-filter, которого этот
 * файл нигде не моделирует). Формулу альфа-смешения (blendHexOverBackground)
 * тоже применяем — solid с opacity < 1 не редкость — но композиция считается
 * только против переданных `pageBackgrounds`, БЕЗ учёта фото-фона экрана
 * (та же оговорка, что у измеримых фонов выше по файлу).
 */
export interface BlockBackgroundRisk {
  screenId: string;
  blockId: string;
  ratio: number;
}

interface RiskCheckBlock {
  id: string;
  background?: { style: string; color?: string; opacity?: number };
}

interface RiskCheckScreen {
  id: string;
  blocks: readonly RiskCheckBlock[];
}

export function findRiskyManualBlockBackgrounds(
  screens: readonly RiskCheckScreen[],
  nextTextPrimary: string,
  pageBackgrounds: readonly string[],
): BlockBackgroundRisk[] {
  if (measurableBackgrounds(nextTextPrimary).length === 0) return [];

  const risky: BlockBackgroundRisk[] = [];
  for (const screen of screens) {
    for (const block of screen.blocks) {
      const bg = block.background;
      if (!bg || bg.style !== 'solid' || !bg.color) continue;
      if (measurableBackgrounds(bg.color).length === 0) continue;

      const bases = pageBackgrounds.length > 0 ? pageBackgrounds : [bg.color];
      const opacity = bg.opacity ?? 1;
      const ratio = Math.min(
        ...bases.map((base) => {
          const blended = blendHexOverBackground(bg.color as string, opacity, base) ?? (bg.color as string);
          return contrastRatio(nextTextPrimary, blended);
        }),
      );

      if (ratio < SURFACE_TEXT_LEGIBILITY_FLOOR) {
        risky.push({ screenId: screen.id, blockId: block.id, ratio });
      }
    }
  }
  return risky;
}
